import Phaser from 'phaser';
import { AudioSystem } from '../systems/AudioSystem';

export interface MissionFailSceneData {
  reason?: string;
}

export class MissionFailScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MissionFailScene' });
  }

  create(data?: MissionFailSceneData): void {
    const { width, height } = this.scale;
    const reason = data?.reason ?? 'All soldiers have fallen.';

    this.cameras.main.fadeIn(700, 0, 0, 0);

    // Play mission fail jingle
    try {
      const audio = new AudioSystem(this);
      audio.playMissionFail();
    } catch (_) { /* AudioContext may not be available yet */ }

    // Dark red background
    this.add.rectangle(0, 0, width, height, 0x100505).setOrigin(0, 0);

    // Red top/bottom stripes
    this.add.rectangle(0, 0, width, 6, 0xff2222).setOrigin(0, 0);
    this.add.rectangle(0, height - 6, width, 6, 0xff2222).setOrigin(0, 0);

    // Skull/cross icon (simple text glyph)
    this.add
      .text(width / 2, height / 2 - 110, '✝', {
        fontSize: '36px',
        color: '#ff4444',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    // Title
    this.add
      .text(width / 2, height / 2 - 60, 'MISSION FAILED', {
        fontSize: '32px',
        color: '#ff4444',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 5,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    // Reason
    this.add
      .text(width / 2, height / 2 - 10, reason, {
        fontSize: '16px',
        color: '#cc8888',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    // If launched from the map editor, show a back-to-editor button instead of retry
    if (sessionStorage.getItem('ff_editor_test_mode') === '1') {
      sessionStorage.removeItem('ff_editor_test_mode');
      this.createButton(width / 2, height / 2 + 80, '← BACK TO EDITOR', 0x00ff88, 0x003322, () => {
        this.cameras.main.fadeOut(500, 0, 0, 0);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.wake('MapEditorScene');
          this.scene.stop('MissionFailScene');
        });
      });
      return;
    }

    // Retry button
    this.createButton(width / 2, height / 2 + 80, 'RETRY MISSION', 0xff4444, 0x330000, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        // Show memorial for the fallen, then reset roster at BriefingScene
        this.scene.start('MemorialScene', { nextScene: 'BriefingScene', resetRoster: true });
      });
    });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    textColor: number,
    bgColor: number,
    onClick: () => void
  ): void {
    const btnWidth = 220;
    const btnHeight = 44;

    const bg = this.add
      .rectangle(x, y, btnWidth, btnHeight, bgColor)
      .setStrokeStyle(2, textColor)
      .setInteractive({ useHandCursor: true });

    const text = this.add
      .text(x, y, label, {
        fontSize: '16px',
        color: `#${textColor.toString(16).padStart(6, '0')}`,
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    bg.on('pointerover', () => {
      bg.setFillStyle(textColor);
      text.setColor(`#${bgColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(bgColor);
      text.setColor(`#${textColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerdown', onClick);
  }
}
